import LoadingIndicator from "@src/Components/LoadingIndicator"
import { createFilename } from "@src/filepath"
import * as React from "react"
import { useSelector } from "react-redux"
import { Redirect, useLocation, useRouteMatch } from "react-router-dom"

import C from "../../../../Controller"
import { tState } from "../../../../Model/Model"
import VerifyEmail from "../VerifyEmail"

const __filename = createFilename("Portal/Accounts/VerifyEmailContainer.tsx")

const RESEND_COOLDOWN_MS = 60000 // 1 minute cooldown
interface IState {
  email: string
}

const VerifyEmailContainer = () => {
  const match = useRouteMatch<{ subscriptionId?: string }>(
    "/verify/:subscriptionId",
  )
  const location = useLocation<IState>()
  const [goToLogin, setGoToLogin] = React.useState(false)
  const [goToSignUp, setGoToSignUp] = React.useState(false)
  const [isResendDisabled, setIsResendDisabled] = React.useState(true)
  const [resendTooltip, setResendTooltip] = React.useState(
    "Please wait 60 seconds before requesting another email",
  )
  const authenticated = useSelector(
    (store: tState) => store.portal.authenticated,
  )

  const checkResendCooldown = React.useCallback(() => {
    const lastResendTime = localStorage.getItem("lastResendTime")
    let pageLoadTime = localStorage.getItem("verifyPageLoadTime")
    const now = Date.now()

    // Set page load time on first visit to this verification page
    if (!pageLoadTime) {
      pageLoadTime = now.toString()
      localStorage.setItem("verifyPageLoadTime", pageLoadTime)
      // Clear any old resend time when first visiting the page
      localStorage.removeItem("lastResendTime")
    }

    // Determine which timestamp to use for cooldown calculation
    let referenceTime = parseInt(pageLoadTime)

    // If user has manually resent during this session, use that time instead
    if (lastResendTime) {
      const lastResendTimeMs = parseInt(lastResendTime)
      const pageLoadTimeMs = parseInt(pageLoadTime)

      // Only use lastResendTime if it's after the current page load
      if (lastResendTimeMs > pageLoadTimeMs) {
        referenceTime = lastResendTimeMs
      }
    }

    const timeSinceReference = now - referenceTime

    if (timeSinceReference < RESEND_COOLDOWN_MS) {
      const remainingSeconds = Math.ceil(
        (RESEND_COOLDOWN_MS - timeSinceReference) / 1000,
      )
      setIsResendDisabled(true)
      setResendTooltip(
        `Please wait ${remainingSeconds} seconds before requesting another email`,
      )
    } else {
      setIsResendDisabled(false)
      setResendTooltip("")
    }
  }, [])

  const requestCodeAgain = React.useCallback(
    (username: string) => {
      if (isResendDisabled) {
        return
      }

      C.Cognito.resendConfirmationCode(
        username,
        (res: { success: boolean; error?: unknown }) => {
          if (res.success) {
            localStorage.setItem("lastResendTime", Date.now().toString())
            setIsResendDisabled(true)
            setResendTooltip(
              "Please wait 60 seconds before requesting another email",
            )
            C.toastNotification(
              "Verification email has been sent. Please check your email.",
              "success",
            )
          } else {
            C.serverLog({
              level: "error",
              message: "Resending verification code Failed",
              endpoint: "cognitoUser.resendConfirmationCode",
              filename: __filename,
              stack: res.error || "",
            })
            C.toastNotification(
              "Unable to resend verification email. Please try again later.",
              "danger",
            )
          }
        },
      )
    },
    [isResendDisabled],
  )

  React.useEffect(() => {
    C.initEnvironment()
  }, [])

  React.useEffect(() => {
    const handleResize = () => {} // kept for resize event registration
    window.addEventListener("resize", handleResize)
    C.Portal.checkSession()

    // Store verification email in localStorage if available from router state
    const emailFromRouter = location?.state?.email
    const storedEmail = localStorage.getItem("verificationEmail")

    if (emailFromRouter) {
      // If email changed, reset cooldown (new verification email was sent)
      if (emailFromRouter !== storedEmail) {
        localStorage.setItem("verificationEmail", emailFromRouter)
        // Reset cooldown timers for new email
        localStorage.removeItem("verifyPageLoadTime")
        localStorage.removeItem("lastResendTime")
      }
    } else if (!storedEmail) {
      setGoToLogin(true)
    }

    checkResendCooldown()

    // Check cooldown every second to update button state
    const cooldownCheckInterval = setInterval(checkResendCooldown, 1000)

    return () => {
      window.removeEventListener("resize", handleResize)
      clearInterval(cooldownCheckInterval)
      // Clean up page load time when leaving verification page
      localStorage.removeItem("verifyPageLoadTime")
      // Clear verification email from localStorage when leaving the page
      localStorage.removeItem("verificationEmail")
    }
  }, [checkResendCooldown, location?.state?.email])

  const app = document.getElementById("portal")

  if (!app) {
    return "Missing Content..."
  }

  if (authenticated === null) {
    return <LoadingIndicator text="Loading Cyber Safety..." />
  }

  if (authenticated || goToLogin || goToSignUp) {
    const subscriptionId = match?.params?.subscriptionId
    return goToSignUp ? (
      <Redirect to={`/signup/${subscriptionId || ""}`} />
    ) : (
      <Redirect to={subscriptionId ? `/signin/${subscriptionId}` : "/"} />
    )
  }

  // Use router state email first, fallback to localStorage email
  const storedEmail = localStorage.getItem("verificationEmail")
  const email = location?.state?.email || storedEmail

  return (
    <VerifyEmail
      height={app.clientHeight}
      onVerifyEmail={requestCodeAgain}
      showSupportModal={() => C.showSupportModal()}
      redirectToSignup={() => setGoToSignUp(true)}
      emailToVerify={email}
      isResendDisabled={isResendDisabled}
      resendTooltip={resendTooltip}
    />
  )
}

export default VerifyEmailContainer
